import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../Components/Header/Header';
import StoreManagement from './StoreManagement';
import OrderManagement from './OrderManagement';
import { useSelector } from 'react-redux';
import { useDispatch } from '../../Redux/CustomHooks';
import { RootState } from '../../Redux/Store';
import { fetchActiveStore, acknowledgeCheckoutRequest } from '../../Redux/ActiveStore/activeStoreApi';
import OrderNotification from '../../Components/OrderNotification';
import CheckoutRequestStatusBar from '../../Components/Order/CheckoutRequestStatusBar';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';
import {
  Box,
  Container,
  ButtonGroup,
  Button,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';

const ActiveStore: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const clerk_id = localStorage.getItem('clerk_id') ?? undefined;
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<'store' | 'order'>('store');
  const activeStoreId = useSelector(
    (state: RootState) => state.activeStore.store?.activeStoreId
  );
  // 常設バー（ADR 0069）はタブをまたいで出すため、タイル表示と同じ Redux の稼働セッションを参照する
  const activeTables = useSelector(
    (state: RootState) => state.activeStore.store?.activeTables
  );
  // 受領確認ポップアップの対象。null の間はダイアログを出さない。
  const [acknowledgeTarget, setAcknowledgeTarget] = useState<{
    activeDiningTableId: string;
    tableName: string;
    paymentMethod: string | null;
  } | null>(null);
  const [acknowledgeError, setAcknowledgeError] = useState<string | null>(null);
  const [orderNotificationMessages, setOrderNotificationMessages] = useState<{
    storeId: string;
    activeStoreId: string;
    activeDiningTableId: string;
    notificationType: string;
    activeOrderId: string; // activeOrderIdを追加
  }[]>([]);

  // 処理済み通知を追跡するための状態
  const [processedNotifications, setProcessedNotifications] = useState<Set<string>>(new Set());
  
  const handleOrderNotificationMessages = (
    storeId: string,
    activeStoreId: string,
    activeDiningTableId: string,
    notificationType: string,
    activeOrderId?: string
  ) => {
    console.log('Received notification in ActiveStore:', { storeId, activeStoreId, activeDiningTableId, notificationType, activeOrderId });

    // 会計リクエスト / 受領・取消は「テーブルの状態」なので、稼働セッションを再取得してタイル表示を通知と同時に揃える（ADR 0068）
    if ((notificationType === 'checkoutRequest' || notificationType === 'checkoutAcknowledged') && store_id) {
      dispatch(fetchActiveStore(store_id));
    }
    
    // activeOrderIdがない場合はactiveStoreIdを使用
    const orderId = activeOrderId || activeStoreId;
    
    // ActiveOrderIdを含めた一意の識別子を生成
    const notificationKey = `${storeId}-${activeStoreId}-${activeDiningTableId}-${notificationType}-${orderId}`;
    
    // まだ処理されていない通知の場合のみ処理
    if (!processedNotifications.has(notificationKey)) {
      // 新しい通知メッセージを作成（ActiveOrderIdを含む）
      const newMessage = { 
        storeId, 
        activeStoreId, 
        activeDiningTableId, 
        notificationType,
        activeOrderId: orderId // ActiveOrderIdを追加
      };
      
      // 通知を配列に追加する
      setOrderNotificationMessages((prevMessages) => {
        // 新しい配列を作成して返す
        const newMessages = [...prevMessages, newMessage];
        console.log('Updated notification messages in ActiveStore:', newMessages);
        
        // 配列が大きくなりすぎないように、最新の10件だけを保持する
        if (newMessages.length > 10) {
          return newMessages.slice(-10);
        }
        
        return newMessages;
      });
      
      // 処理済み通知のセットを更新
      setProcessedNotifications(prev => {
        const newSet = new Set(prev);
        newSet.add(notificationKey);
        return newSet;
      });
    } else {
      console.log('Notification already processed in ActiveStore, skipping:', notificationKey);
    }
  };

  // 常設バーからの受領（ADR 0069）。成功・失敗にかかわらず再取得し、他端末が先に受領していた場合も表示を揃える。
  const handleConfirmAcknowledge = () => {
    if (!store_id || !activeStoreId || !acknowledgeTarget) return;
    const { activeDiningTableId } = acknowledgeTarget;
    setAcknowledgeTarget(null);
    dispatch(
      acknowledgeCheckoutRequest({
        activeStoreId,
        activeDiningTableId,
      })
    ).then((action) => {
      if (acknowledgeCheckoutRequest.rejected.match(action)) {
        const payload = action.payload;
        setAcknowledgeError(
          typeof payload === 'string' && payload
            ? payload
            : '会計リクエストの受領に失敗しました'
        );
      }
      dispatch(fetchActiveStore(store_id));
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header />
      <CheckoutRequestStatusBar
        activeTables={activeTables ?? []}
        onSelect={(activeDiningTableId, tableName, paymentMethod) =>
          setAcknowledgeTarget({ activeDiningTableId, tableName, paymentMethod })
        }
      />
      <ConfirmDialog
        open={acknowledgeTarget !== null}
        onClose={() => setAcknowledgeTarget(null)}
        onConfirm={handleConfirmAcknowledge}
        title="会計リクエストを受領しますか？"
        message={
          acknowledgeTarget
            ? `${acknowledgeTarget.tableName}${
                acknowledgeTarget.paymentMethod
                  ? `（お支払い方法: ${acknowledgeTarget.paymentMethod}）`
                  : ''
              }の会計リクエストを受領します。受領するとお客様の追加注文は締め切られます。`
            : ''
        }
        confirmText="受領する"
      />
      <Snackbar
        open={acknowledgeError !== null}
        autoHideDuration={6000}
        onClose={() => setAcknowledgeError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setAcknowledgeError(null)}>
          {acknowledgeError}
        </Alert>
      </Snackbar>
      <Container maxWidth="lg" sx={{ py: 3, px: { xs: 0, sm: 0 }, mx: 'auto' }}>
        <Paper elevation={0} sx={{ p: 1, maxWidth: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <ButtonGroup variant="contained" size="large">
              <Button
                onClick={() => setActiveTab('store')}
                variant={activeTab === 'store' ? 'contained' : 'outlined'}
                sx={{
                  bgcolor: activeTab === 'store' ? 'primary.main' : 'background.paper',
                  color: activeTab === 'store' ? 'common.white' : 'text.primary',
                  '&:hover': {
                    bgcolor: activeTab === 'store' ? 'primary.dark' : 'background.default',
                  },
                }}
              >
                店舗管理
              </Button>
              <Button
                onClick={() => setActiveTab('order')}
                variant={activeTab === 'order' ? 'contained' : 'outlined'}
                sx={{
                  bgcolor: activeTab === 'order' ? 'primary.main' : 'background.paper',
                  color: activeTab === 'order' ? 'common.white' : 'text.primary',
                  '&:hover': {
                    bgcolor: activeTab === 'order' ? 'primary.dark' : 'background.default',
                  },
                }}
              >
                注文管理
              </Button>
            </ButtonGroup>

            <Box>
              <OrderNotification
                storeId={store_id!}
                clerkId={clerk_id}
                onNotificationMessages={handleOrderNotificationMessages}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            {activeTab === 'store' ? (
              <StoreManagement />
            ) : (
              <OrderManagement
                activeStoreId={activeStoreId}
                orderNotificationData={orderNotificationMessages}
              />
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ActiveStore;
